import type { AnyNetworkElement, Hausanschluss, NetworkElementType } from '../../../types/network';
import { useNetworkUIStore } from '../../../store/networkUIStore';
import { useTabStore } from '../../../store/tabStore';
import { fmt } from '../../../utils/numberFormat';
import { PU_BASE_LN_V } from '../../../utils/electricalConstants';
import { elementDisplayName } from '../../../utils/elementDisplayName';

// ─── Loading color helpers ─────────────────────────────────────────

interface LoadLevels {
  yellow: number;
  orange: number;
  red: number;
}

export function getLoadingColor(percent: number | null, levels: LoadLevels): string {
  if (percent == null) return 'text-gray-400 dark:text-primary-500';

  if (percent >= levels.red) return 'text-red-600 dark:text-red-400';
  if (percent >= levels.orange) return 'text-orange-600 dark:text-orange-400';
  if (percent >= levels.yellow) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-emerald-600 dark:text-emerald-400';
}

export function getLoadingBgColor(percent: number | null, levels: LoadLevels): string {
  if (percent == null) return 'bg-gray-200 dark:bg-primary-600';

  if (percent >= levels.red) return 'bg-red-500';
  if (percent >= levels.orange) return 'bg-orange-500';
  if (percent >= levels.yellow) return 'bg-yellow-500';
  return 'bg-emerald-500';
}

// ─── Voltage helpers ───────────────────────────────────────────────

// pu ↔ V always on the load-flow base (400 V/√3); Un = 230 V is only the
// reference for deviations and limits — see utils/electricalConstants.ts.
export function puToVolts(pu: number | null): number | null {
  if (pu == null) return null;
  return pu * PU_BASE_LN_V;
}

export function isVoltageOutOfRange(
  pu: number | null,
  minPu: number,
  maxPu: number,
): boolean {
  if (pu == null) return false;
  return pu < minPu || pu > maxPu;
}

// ─── Formatters ────────────────────────────────────────────────────

export function fmtKw(val: number | null): string {
  if (val == null) return '—';
  return `${fmt(Math.abs(val), 1)} kW`;
}

export function fmtPercent(val: number | null): string {
  if (val == null) return '—';
  return `${fmt(val, 1)}%`;
}

export function fmtAmps(val: number | null): string {
  if (val == null) return '—';
  return `${fmt(val, 1)} A`;
}

export function fmtVolts(pu: number | null): string {
  if (pu == null) return '—';
  return `${fmt((pu * PU_BASE_LN_V), 1)} V`;
}

export function fmtTimestamp(ts: number | null): string {
  if (!ts) return '—';
  const diff = Date.now() - ts;
  if (diff < 60_000) return `vor ${Math.floor(diff / 1000)} s`;
  if (diff < 3_600_000) return `vor ${Math.floor(diff / 60_000)} Min.`;
  if (diff < 86_400_000) return `vor ${Math.floor(diff / 3_600_000)} Std.`;
  return new Date(ts).toLocaleString('de-DE', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

// ─── Display name ──────────────────────────────────────────────────

export function getDisplayName(
  el: AnyNetworkElement,
  resolve?: (id: string) => AnyNetworkElement | undefined,
): string {
  if (el.type === 'hausanschluss' && !el.name) {
    const ha = el as Hausanschluss;
    const parts: string[] = [];
    if (ha.street) parts.push(ha.street);
    if (ha.houseNumber) parts.push(ha.houseNumber);
    if (ha.city) parts.push(ha.city);
    if (parts.length > 0) return parts.join(' ');
  }
  // Cables named after their import id get "<Typ> · <von> → <nach>".
  if (el.type === 'abgangsleitung') return elementDisplayName(el, resolve);
  return el.name || el.id;
}

// ─── Element type display ──────────────────────────────────────────

export const TYPE_LABELS: Record<NetworkElementType, string> = {
  trafostation: 'Trafo',
  kabelverteilschrank: 'KVS',
  hausanschluss: 'HA',
  abgangsleitung: 'Leitung',
  muffe: 'Muffe',
};

export const TYPE_LETTERS: Record<NetworkElementType, string> = {
  trafostation: 'T',
  kabelverteilschrank: 'K',
  hausanschluss: 'H',
  abgangsleitung: 'L',
  muffe: 'M',
};

// ─── Navigate to element on map ────────────────────────────────────

export function navigateToElement(element: AnyNetworkElement): void {
  const uiStore = useNetworkUIStore.getState();
  const tabStore = useTabStore.getState();

  // Open/activate map tab
  tabStore.openTab('map');

  // Select element
  uiStore.setSelectedElement(element);

  // Set map view after a tick (ensure map tab is active)
  setTimeout(() => {
    if (element.type === 'abgangsleitung') {
      const coords = element.waypoints.length > 0
        ? { lat: element.waypoints[0].lat, lng: element.waypoints[0].lng }
        : element.segments.length > 0
          ? element.segments[0].startCoordinate
          : element.coordinates;
      uiStore.setMapView(coords, 20);
    } else if ('coordinates' in element) {
      uiStore.setMapView(element.coordinates, 20);
    }

    uiStore.setHighlightedElement(element.id);
    setTimeout(() => uiStore.setHighlightedElement(null), 3000);
  }, 50);
}

// ─── Resolve monitoring/diagnostic elementId → AnyNetworkElement ────
//
// The powerflow service reports element IDs that can be:
//   - a bus ext_id (UUID)            → match element.id directly
//   - a cable ext_id + "_segN" suffix → strip, then check element.id OR
//                                       iterate elements[].segments[]
//   - a ring synthetic ID            → no element match; caller falls
//                                       back to the name column
//
// Returns null when the ID can't be resolved.
export function findElementByMonitoringId(
  elementId: string | null | undefined,
  elements: AnyNetworkElement[],
): AnyNetworkElement | null {
  if (!elementId) return null;

  // Strip `_seg<number>` suffix produced by the monitoring layer
  const cleanId = elementId.replace(/_seg\d+$/, '');

  // Fast path: direct element.id match (Hausanschluss, Trafo, KVS, Muffe,
  // or top-level Abgangsleitung).
  const direct = elements.find(e => e.id === cleanId);
  if (direct) return direct;

  // Slow path: scan every Abgangsleitung's segments for a matching
  // segment.id — these belong to the parent cable.
  for (const el of elements) {
    if (el.type !== 'abgangsleitung') continue;
    if (el.segments.some(s => s.id === cleanId)) return el;
  }
  return null;
}

export function navigateToElementById(
  elementId: string | null | undefined,
  elements: AnyNetworkElement[],
): boolean {
  const el = findElementByMonitoringId(elementId, elements);
  if (!el) return false;
  navigateToElement(el);
  return true;
}
